import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import {
  CalendarPlus,
  Loader2,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  ThumbsDown,
  ThumbsUp,
  type LucideIcon,
  Voicemail,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { logAction } from "@/api/activity";
import { updateCompany } from "@/api/companies";
import { createTask } from "@/api/tasks";
import { scoreColor } from "@/lib/constants";
import { useAuthStore } from "@/stores/auth.store";
import { combineDateTime } from "@/components/tasks/taskFormatters";
import { useTaskStatuses } from "@/hooks/useTaskStatuses";
import { useDefaultTimezone } from "@/hooks/useDefaultTimezone";
import { toNaiveIsoDateTime } from "@/lib/timezones";
import type {
  CompanyDetail,
  EmbeddedContact,
  PipelineStatus,
  TaskCreate,
} from "@/api/types";

const DURATIONS = [5, 10, 15, 30, 45, 60] as const;

const OUTCOMES: {
  key: string;
  label: string;
  icon: LucideIcon;
  tone: "positive" | "negative" | "neutral";
}[] = [
  { key: "interested", label: "Interested", icon: ThumbsUp, tone: "positive" },
  {
    key: "not_interested",
    label: "Not interested",
    icon: ThumbsDown,
    tone: "negative",
  },
  { key: "callback", label: "Callback", icon: Clock, tone: "neutral" },
  { key: "voicemail", label: "Voicemail", icon: Voicemail, tone: "neutral" },
  {
    key: "no_answer",
    label: "No answer",
    icon: PhoneMissed,
    tone: "neutral",
  },
  {
    key: "wrong_number",
    label: "Wrong number",
    icon: PhoneOff,
    tone: "negative",
  },
];

const TONE_CLASSES: Record<string, string> = {
  positive:
    "border-emerald-200 bg-emerald-50 text-emerald-700 ring-emerald-300",
  negative: "border-rose-200 bg-rose-50 text-rose-700 ring-rose-300",
  neutral: "border-slate-200 bg-slate-50 text-slate-700 ring-slate-300",
};

interface CallLogFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectSlug: string;
  company: CompanyDetail;
  pipelineStatuses: PipelineStatus[];
  onLogged: () => void;
  /** When set, the form opens "from-timer" mode: contact is locked,
   *  duration prefilled with the measured value, and the start /
   *  end timestamps render in muted text above the duration row.
   *  All four fields move together — passing only some has no effect. */
  prefill?: {
    contactId: number;
    contactName: string;
    durationMinutes: number;
    startedAt: Date;
    endedAt: Date;
  } | null;
}

const KEEP_STATUS = "__keep__";

export function CallLogForm({
  open,
  onOpenChange,
  projectSlug,
  company,
  pipelineStatuses,
  onLogged,
  prefill,
}: CallLogFormProps) {
  const currentUser = useAuthStore((s) => s.user);
  const taskStatuses = useTaskStatuses(projectSlug);
  const defaultTimezone = useDefaultTimezone();
  // Pre-pick the decision-maker when present so the dropdown
  // surfaces the most likely contact first; fall back to the first
  // contact when the company has no flagged DM.
  const initialContactId = (() => {
    const dm = company.contacts.find((c) => c.is_decision_maker);
    return dm?.id
      ? String(dm.id)
      : company.contacts[0]?.id
        ? String(company.contacts[0].id)
        : null;
  })();
  const [contactId, setContactId] = useState<string | null>(
    prefill ? String(prefill.contactId) : initialContactId,
  );
  const [duration, setDuration] = useState<number>(
    prefill?.durationMinutes ?? 15,
  );
  const [outcome, setOutcome] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [statusAfter, setStatusAfter] = useState<string>(KEEP_STATUS);
  // "Schedule a follow-up" — when on, submit also creates a follow_up
  // Task linked to the company, due on the picked date. Defaults to
  // two days out so the picker isn't blank on first open.
  const [followUp, setFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<string>(() =>
    format(addDays(new Date(), 2), "yyyy-MM-dd"),
  );
  const [submitting, setSubmitting] = useState(false);

  // Re-seed mutable state when the dialog reopens or the prefill
  // payload changes so a stop-the-timer flow always lands on fresh
  // values instead of leftover state from the last open.
  useEffect(() => {
    if (!open) return;
    setFollowUpDate(format(addDays(new Date(), 2), "yyyy-MM-dd"));
    if (prefill) {
      setContactId(String(prefill.contactId));
      setDuration(prefill.durationMinutes);
    }
    // initialContactId / DURATIONS are stable; we intentionally do
    // not reset other fields (notes / outcome) on every open so a
    // user re-opening doesn't lose what they typed mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  function reset() {
    setContactId(initialContactId);
    setDuration(15);
    setOutcome(null);
    setNotes("");
    setStatusAfter(KEEP_STATUS);
    setFollowUp(false);
    setFollowUpDate(format(addDays(new Date(), 2), "yyyy-MM-dd"));
  }

  async function handleSubmit() {
    if (!outcome) {
      toast.error("Pick an outcome before saving.");
      return;
    }
    if (followUp && !followUpDate) {
      toast.error("Pick a date for the follow-up.");
      return;
    }
    setSubmitting(true);
    try {
      await logAction(projectSlug, company.id, {
        action: "call_logged",
        details: {
          duration_minutes: duration,
          outcome,
          notes: notes.trim() || undefined,
          contact_id: contactId ? Number(contactId) : undefined,
          contact_name: selectedContact?.full_name,
          has_follow_up: followUp,
          started_at: prefill?.startedAt.toISOString(),
          ended_at: prefill?.endedAt.toISOString(),
        },
      });
      if (statusAfter !== KEEP_STATUS && statusAfter !== company.pipeline_status) {
        await updateCompany(projectSlug, company.id, {
          pipeline_status: statusAfter,
        });
      }
      // Mirror the call as a Task with status=done so it shows up in
      // the kanban "Done" column, on the calendar at the time it
      // happened, and in any task-based reporting. Best-effort —
      // the activity log is already saved by this point, so a task-
      // create failure surfaces a soft warning rather than rolling
      // back the whole submit.
      const doneKey = Array.from(taskStatuses.doneKeys)[0];
      if (doneKey && currentUser?.id) {
        try {
          // Manual flow has no measured start time — back-calculate
          // from "now − duration" so the task lands at a plausible
          // moment instead of midnight UTC.
          const startedAt =
            prefill?.startedAt ??
            new Date(Date.now() - duration * 60_000);
          const naiveStart = toNaiveIsoDateTime(
            formatDatePart(startedAt),
            formatTimePart(startedAt),
          );
          const outcomeLabel =
            OUTCOMES.find((o) => o.key === outcome)?.label ?? outcome;
          const taskPayload: TaskCreate = {
            title: `Call with ${selectedContact?.full_name ?? "contact"} · ${company.name}`,
            description:
              `Outcome: ${outcomeLabel}` +
              (notes.trim() ? `\n\n${notes.trim()}` : ""),
            company_id: company.id,
            assigned_to_id: currentUser.id,
            priority: "medium",
            status: doneKey,
            task_type: "call",
            contact_id: contactId ? Number(contactId) : null,
            scheduled_at: naiveStart,
            due_at: naiveStart,
            scheduled_timezone: defaultTimezone,
            duration_minutes: duration,
          };
          await createTask(projectSlug, taskPayload);
        } catch {
          toast.warning(
            "Call logged, but couldn't mirror it as a Done task.",
          );
        }
      }
      // Follow-up Task creation. Best-effort — the call was already
      // logged, so a transient task-create failure shouldn't undo
      // that. We surface a toast and continue.
      if (followUp) {
        try {
          const dueAt = combineDateTime(followUpDate, null);
          const contactName = selectedContact?.full_name ?? "the contact";
          const outcomeLabel =
            OUTCOMES.find((o) => o.key === outcome)?.label.toLowerCase() ??
            outcome;
          const today = format(new Date(), "MMM d");
          const payload: TaskCreate = {
            title: `Follow up with ${contactName} · ${company.name}`,
            description:
              `Follow up after call on ${today}. Outcome: ${outcomeLabel}.` +
              (notes.trim() ? `\n\n${notes.trim()}` : ""),
            company_id: company.id,
            assigned_to_id: currentUser?.id ?? "",
            due_at: dueAt,
            priority: "medium",
            task_type: "follow_up",
            contact_id: contactId ? Number(contactId) : null,
          };
          if (!payload.assigned_to_id) {
            // Best-effort guard — if we somehow lost the user, skip
            // the task rather than fail validation.
            throw new Error("No current user");
          }
          await createTask(projectSlug, payload);
        } catch {
          toast.warning(
            "Call logged, but couldn't create the follow-up task.",
          );
        }
      }
      toast.success("Call logged");
      onLogged();
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedContact = company.contacts.find(
    (c) => String(c.id) === contactId,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Log a call</SheetTitle>
          <SheetDescription className="sr-only">
            Record a call outcome for {company.name}.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-1 py-4">
          {/* Company chip */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="flex items-baseline justify-between">
              <div className="font-medium text-slate-900">{company.name}</div>
              <span
                className={cn("text-sm font-semibold", scoreColor(company.score))}
              >
                {company.score ?? "—"}
              </span>
            </div>
            {selectedContact && (
              <div className="mt-3 flex items-center gap-2">
                <Avatar className="h-7 w-7 border border-slate-200">
                  <AvatarFallback className="bg-slate-900 text-[10px] font-semibold">
                    {initials(selectedContact.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 text-sm">
                  <div className="truncate text-slate-700">
                    <span className="font-medium">{selectedContact.full_name}</span>
                    {selectedContact.title && (
                      <span className="ml-1 text-slate-500">
                        · {selectedContact.title}
                      </span>
                    )}
                  </div>
                  {selectedContact.email && (
                    <div className="truncate text-xs text-indigo-600">
                      {selectedContact.email}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Contact selector — locked when prefilled by the timer
           *  (the user is logging the exact call they just dialled,
           *  so changing the contact mid-form would create a
           *  mismatch with the activity record). */}
          {prefill ? (
            <Field label="Contact">
              <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm text-slate-700">
                <span className="truncate font-medium">
                  {prefill.contactName}
                </span>
              </div>
            </Field>
          ) : (
            company.contacts.length > 1 && (
              <Field label="Contact">
                <Select
                  value={contactId ?? ""}
                  onValueChange={(v) => setContactId(v)}
                >
                  <SelectTrigger className="h-10 rounded-lg border-slate-200">
                    <SelectValue placeholder="Pick a contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {company.contacts.map((c: EmbeddedContact) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.full_name}
                        {c.title && ` · ${c.title}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )
          )}

          {/* Time range — shown when the dialog opened from a stopped
           *  timer. Read-only; the start / end stay locked to what
           *  the timer measured so the activity record is the truth. */}
          {prefill && (
            <div className="rounded-lg border border-slate-900/[0.06] bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
              <span className="font-medium text-slate-600">Started:</span>{" "}
              {format(prefill.startedAt, "MMM d, HH:mm:ss")}
              {"  ·  "}
              <span className="font-medium text-slate-600">Ended:</span>{" "}
              {format(prefill.endedAt, "HH:mm:ss")}
            </div>
          )}

          {/* Duration — preset buttons in the manual flow; tweakable
           *  number input when prefilled by a timer (the measured
           *  value rarely lands on a preset like 5 / 10 / 30). */}
          <Field label="Duration">
            {prefill ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={duration}
                  onChange={(e) =>
                    setDuration(
                      Math.max(1, Number(e.target.value) || 1),
                    )
                  }
                  className="h-9 w-[88px] rounded-lg border border-slate-200 px-3 text-sm tabular-nums focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-200"
                />
                <span className="text-sm text-slate-500">minutes</span>
                <span className="ml-1 text-[11px] text-slate-400">
                  (timer measured {prefill.durationMinutes} min, adjust
                  if needed)
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDuration(d)}
                    className={cn(
                      "h-9 rounded-full border px-4 text-sm transition-colors",
                      duration === d
                        ? "border-indigo-500 bg-indigo-500 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  )}
                  >
                    {d} min
                  </button>
                ))}
              </div>
            )}
          </Field>

          {/* Outcome */}
          <Field label="Outcome">
            <div className="grid grid-cols-2 gap-2">
              {OUTCOMES.map((o) => {
                const Icon = o.icon;
                const active = outcome === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setOutcome(o.key)}
                    className={cn(
                      "flex items-center gap-2 rounded-xl border px-3 py-3 text-sm transition-all",
                      active
                        ? `${TONE_CLASSES[o.tone]} ring-2 ring-offset-1`
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Notes */}
          <Field label="Notes">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was the conversation about?"
              className="min-h-[100px] rounded-lg border-slate-200"
            />
          </Field>

          {/* Schedule follow-up — checkbox + date picker. When on, the
           *  submit handler also creates a follow_up Task linked to
           *  the company so the call has a natural next step on the
           *  calendar. */}
          <div className="space-y-2">
            <label
              htmlFor="call-followup"
              className="flex cursor-pointer select-none items-center gap-2 text-sm text-slate-700"
            >
              <input
                id="call-followup"
                type="checkbox"
                checked={followUp}
                onChange={(e) => setFollowUp(e.target.checked)}
                className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-300"
              />
              <CalendarPlus className="h-3.5 w-3.5 text-slate-500" />
              Schedule a follow-up
            </label>
            {followUp && (
              <div className="ml-6">
                <Input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="h-9 w-[180px] rounded-lg border-slate-200"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Creates a follow-up task linked to {company.name}.
                </p>
              </div>
            )}
          </div>

          {/* Optional new status */}
          <Field label="Update status (optional)">
            <Select value={statusAfter} onValueChange={setStatusAfter}>
              <SelectTrigger className="h-10 rounded-lg border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={KEEP_STATUS}>Keep current</SelectItem>
                {pipelineStatuses
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <SheetFooter className="border-t border-slate-900/[0.06] pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !outcome}
          variant="primary"
          >
            {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            <PhoneCall className="mr-2 h-3.5 w-3.5" />
            Log call
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't log the call.";
}

function formatDatePart(d: Date): string {
  const yyyy = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${m}-${day}`;
}

function formatTimePart(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}
